<script>
import axios from 'axios'
import { format } from 'date-fns'
import InventoryBulkUpdate from '@components/inventory-bulk-update'
import Request from '@src/mixins/request'
import headers from '@src/data/headers.json'

const today = format(new Date(), 'YYYY-MM-DD')

export default {
  metaInfo: {
    title: 'Inventory',
    meta: [{ name: 'description', content: 'Inventory' }],
  },

  $_veeValidate: { validator: 'new' },

  components: { InventoryBulkUpdate },

  mixins: [Request],

  props: {
    roomTypes: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      headers: headers.inventory,
      today,
      color: 'gold',
      inventories: [],
      tableData: [],
      form: {
        id: null,
        dateFrom: null,
        dateTo: null,
      },
    }
  },

  computed: {
    rules() {
      const ids = this.roomTypes.map((r) => r.id)
      return {
        id: `required|included:${ids}`,
        from: 'required|date_format:yyyy-MM-dd',
        to: 'required|date_format:yyyy-MM-dd|after:from,true',
      }
    },
  },

  methods: {
    onSubmit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.getInventories()
        }
      })
    },
    getInventories() {
      this.fetchData({
        url: this.endpoint.inventory.list,
        data: this.form,
      }).then((data) => {
        if (data) {
          this.inventories = data
          this.tableData = data.map((d) => {
            const obj = {}
            Object.keys(d).forEach((key) => {
              let val = d[key]
              const isInclude = ['id', 'date', 'roomTypeId'].includes(key)
              if (!isInclude) {
                val = {
                  value: d[key],
                  loading: false,
                }
              }
              obj[key] = val
            })
            return obj
          })
        }
      })
    },
    updateInventory(field, item, value) {
      const data = item[field]
      const { id } = item

      data.loading = true
      axios({
        url: this.endpoint.inventory.update,
        data: {
          id,
          update: {
            [field]: value,
          },
        },
      })
        .then((res) => {
          data.loading = false
        })
        .catch(() => {
          data.loading = false
          const rec = this.inventories.find((i) => i.id === id)
          data.value = rec[field]
          this.displayMessage('An error was occured. Please try again.')
        })
    },
  },
}
</script>

<template>
  <BaseView title="Inventory" :color="color">
    <!-- Toolbar Extension -->
    <VContainer slot="toolbar-extension" class="px-0" grid-list-xl>
      <VLayout row wrap>
        <VFlex xs12 sm3>
          <VSelect
            v-model.number="form.id"
            v-validate="rules.id"
            data-vv-name="room type"
            :error-messages="errors.collect('room type')"
            :color="color"
            :items="roomTypes"
            label="Room type"
            item-text="name"
            item-value="id"
          />
        </VFlex>
        <VFlex xs4 sm3>
          <BaseDatePicker
            ref="from"
            v-model="form.dateFrom"
            v-validate="rules.from"
            data-vv-name="from"
            :error-messages="errors.collect('from')"
            :min-date="today"
            :color="color"
            label="From"
          />
        </VFlex>
        <VFlex xs4 sm3>
          <BaseDatePicker
            v-model="form.dateTo"
            v-validate="rules.to"
            data-vv-name="to"
            :error-messages="errors.collect('to')"
            label="To"
            :min-date="today"
            :color="color"
          />
        </VFlex>
        <VFlex xs4 sm3>
          <VBtn
            depressed
            :dark="!errors.any()"
            :disabled="errors.any()"
            :color="color"
            @click="onSubmit"
          >
            Search
          </VBtn>
        </VFlex>
      </VLayout>
    </VContainer>
    <!-- End of Toolbar Extension -->

    <!-- Toolbar Actions -->
    <InventoryBulkUpdate slot="toolbar-actions" :room-types="roomTypes">
      <VBtn slot="activator" :color="color" depressed dark>
        Bulk Update
      </VBtn>
    </InventoryBulkUpdate>
    <!-- End of Toolbar Actions -->

    <VDataTable
      :headers="headers"
      :loading="loading ? color : false"
      :items="tableData"
      hide-actions
    >
      <!-- Item Slot -->
      <template slot="items" slot-scope="props">
        <!-- <td
          v-for="(header, index) in headers"
          :key="`${header.text}-${index}`"
          :class="{ 'text-xs-right': header.align && header.align === 'right' }"
        >
          {{ props.item[header.value] }}
        </td> -->
        <td>{{ props.item.date }}</td>
        <td>
          <VTextField
            v-model.number="props.item.rate.value"
            :loading="props.item.rate.loading"
            :disabled="props.item.rate.loading"
            :color="color"
            background-color="transparent"
            solo
            reverse
            flat
            single-line
            hide-details
            @change="updateInventory('rate', props.item, $event)"
          />
        </td>
        <td>
          <VTextField
            v-model.number="props.item.available.value"
            :loading="props.item.available.loading"
            :disabled="props.item.available.loading"
            :color="color"
            background-color="transparent"
            solo
            reverse
            flat
            single-line
            hide-details
            @change="updateInventory('available', props.item, $event)"
          />
        </td>
        <td>
          <VCheckbox
            v-model="props.item.stopSell.value"
            :loading="props.item.stopSell.loading"
            :disabled="props.item.stopSell.loading"
            :color="color"
            style="float: right;"
            hide-details
            @change="updateInventory('stopSell', props.item, $event)"
          />
        </td>
        <td>
          <VCheckbox
            v-model="props.item.cta.value"
            :loading="props.item.cta.loading"
            :disabled="props.item.cta.loading"
            :color="color"
            style="float: right;"
            hide-details
            @change="updateInventory('cta', props.item, $event)"
          />
        </td>
        <td>
          <VCheckbox
            v-model="props.item.ctd.value"
            :loading="props.item.ctd.loading"
            :disabled="props.item.ctd.loading"
            :color="color"
            style="float: right;"
            hide-details
            @change="updateInventory('ctd', props.item, $event)"
          />
        </td>
      </template>
      <!-- End of Item Slot -->
    </VDataTable>
  </BaseView>
</template>
